import { LogoMobile } from '@/common/assets/svg/logoMobile'
import { Button } from '@/common/ui/button'
import { Sheet, SheetContent } from '@/common/ui/sheet'
import { PWA_APP_COPY } from '@/locales/en'
import { Download, Globe, MoreHorizontal, PlusSquare, Share } from 'lucide-react'
import IntercomVisibilityController from '@/common/ui/IntercomVisibilityController'
import { useEffect, useState } from 'react'
import { usePwaContext } from '@/common/pwa/pwaContext'
import { useMobileOS } from '@/common/hooks/useMobileOS'
import InstallAppPromotionShownAnalytics from '../analytics/events/pwa/InstallAppPromotionShownAnalytics'

function AppleInstallInstructions({ onClose }: { onClose: () => void }) {
    const InstructionItem = (props: { icon: React.ReactNode; text: string }) => (
        <div className="self-stretch inline-flex justify-start items-center gap-4">
            {props.icon}
            <div className="self-stretch justify-start text-black text-base font-medium font-['Inter'] leading-6">
                {props.text}
            </div>
        </div>
    )

    return (
        <div className="self-stretch inline-flex flex-col justify-center items-start gap-6">
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />
            <div className="justify-start text-content-secondary text-base font-medium font-['Inter'] leading-6">
                {PWA_APP_COPY.appleInstructions.title}
            </div>
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
                <InstructionItem icon={<Globe size={24} />} text={PWA_APP_COPY.appleInstructions.steps[0]} />
                <InstructionItem icon={<MoreHorizontal size={24} />} text={PWA_APP_COPY.appleInstructions.steps[1]} />
                <InstructionItem icon={<Share size={24} />} text={PWA_APP_COPY.appleInstructions.steps[2]} />
                <InstructionItem icon={<PlusSquare size={24} />} text={PWA_APP_COPY.appleInstructions.steps[3]} />
            </div>
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />
            <Button fullWidth variant="tertiary" size="lg" onClick={onClose}>
                <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                    {PWA_APP_COPY.appleInstructions.button}
                </div>
            </Button>
        </div>
    )
}

export function InstallWebAppSheet(props: { open: boolean; onDismiss: () => void }) {
    const os = useMobileOS()
    const { promptInstall } = usePwaContext()
    const [showAppleInstructions, setShowAppleInstructions] = useState(false)

    useEffect(() => {
        if (!props.open) return
        try {
            const KEY = 'pwa_install_shown_tracked'
            if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(KEY)) return
            InstallAppPromotionShownAnalytics.track({ OS: os })
            if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(KEY, 'true')
        } catch {
            // ignore analytics/session storage errors
        }
    }, [props.open, os])

    return (
        <div className="md:hidden">
            <IntercomVisibilityController open={!!props.open} />
            <Sheet
                open={props.open}
                onOpenChange={(open) => {
                    if (!open) {
                        props.onDismiss()
                    }
                }}
            >
                <SheetContent side={'bottom'} onClose={props.onDismiss} className="w-full">
                    <div className="self-stretch p-6 relative bg-white rounded-tl-2xl rounded-tr-2xl inline-flex flex-col justify-start items-center gap-4">
                        <div className="self-stretch flex flex-col justify-start items-start gap-4">
                            <div className="self-stretch inline-flex justify-start items-start gap-4">
                                <div className="w-64 flex justify-start items-center gap-4">
                                    <div className="p-2.5 bg-brand-primary-forest rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                                        <LogoMobile variant="green" width={32} height={32} />
                                    </div>
                                    <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                                        {PWA_APP_COPY.installTitle}
                                    </div>
                                </div>
                            </div>
                            <div className="self-stretch justify-start text-black text-base font-normal font-['Inter'] leading-6">
                                {PWA_APP_COPY.installDescription}
                            </div>
                            {os === 'ios' && (
                                <div
                                    aria-hidden={!showAppleInstructions}
                                    className={`transform transition duration-300 ease-out ${
                                        showAppleInstructions
                                            ? 'opacity-100 translate-y-0 h-auto'
                                            : 'opacity-0 translate-y-4 h-0 overflow-hidden pointer-events-none'
                                    }`}
                                >
                                    <AppleInstallInstructions
                                        onClose={() => {
                                            setShowAppleInstructions(false)
                                            props.onDismiss()
                                        }}
                                    />
                                </div>
                            )}

                            {!showAppleInstructions && (
                                <Button
                                    fullWidth
                                    variant="secondary"
                                    size="lg"
                                    onClick={async () => {
                                        if (os === 'ios') {
                                            setShowAppleInstructions(true)
                                            return
                                        }
                                        try {
                                            props.onDismiss()
                                            await promptInstall?.()
                                        } catch {
                                            // ignore
                                        }
                                    }}
                                >
                                    <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                                        {PWA_APP_COPY.installCta}
                                    </div>
                                    <Download size={16} />
                                </Button>
                            )}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    )
}
